/**
 * AlgoGreeks Super-Model Dashboard Components
 * 
 * Module 5: "God Mode" Dashboard
 * - GEX Levels Display (Put Wall, Call Wall, Zero Gamma)
 * - Gamma Regime Indicator
 * - Super Signal Panel with Win Probability
 * - Trident Exit Tier Visualization
 */

import React, { memo, useMemo, useRef } from 'react';

// ============================================
// GEX LEVELS PANEL
// ============================================

export const GEXLevelsPanel = memo(({ gexLevels, currentPrice, isAdmin = false }) => {
  // SESSION 99e: Last Known Good (LKG) futures price ref
  const lastKnownGoodPriceRef = useRef(0);
  
  if (!gexLevels) {
    return (
      <div className="panel-content flex items-center justify-center text-muted-foreground">
        <span className="text-sm font-mono">Waiting for GEX calculation...</span>
      </div>
    );
  }

  const { 
    put_wall, call_wall, zero_gamma, gamma_regime, put_wall_gex, call_wall_gex,
    spot_price, futures_price, expiry_date, greeks_source, accelpix_greeks_count, bs_greeks_count,
    is_estimated, is_replay_mode, message
  } = gexLevels;
  
  // === REPLAY MODE: Show indicator only when explicitly frozen ===
  // is_replay_mode alone means GEX is running but with replay data
  const isReplayFrozen = greeks_source === 'REPLAY_FROZEN';
  const isReplayMode = is_replay_mode && !isReplayFrozen;
  
  // SESSION 99e: SSOT for futures price with LKG protection
  // Stricter validation: NIFTY 15000-35000, BANKNIFTY 30000-80000
  let refPrice = futures_price || currentPrice || 0;
  const isValidPrice = refPrice >= 15000 && refPrice <= 80000;
  
  if (isValidPrice) {
    lastKnownGoodPriceRef.current = refPrice;
  } else if (lastKnownGoodPriceRef.current > 0) {
    refPrice = lastKnownGoodPriceRef.current;
  }
  
  // Calculate distances (using futures price as reference for GEX walls)
  const distanceToPutWall = refPrice ? (refPrice - put_wall).toFixed(0) : '-';
  const distanceToCallWall = refPrice ? (call_wall - refPrice).toFixed(0) : '-';
  const distanceToZeroGamma = refPrice ? (refPrice - zero_gamma).toFixed(0) : '-';

  // Calculate total Net GEX for display
  const totalNetGex = (put_wall_gex || 0) + (call_wall_gex || 0);

  const isNegativeGamma = gamma_regime === 'NEGATIVE_GAMMA';
  const isLiveGexSource = greeks_source === 'LIVE_GEX';
  const isEstimated = is_estimated || greeks_source === 'ESTIMATED';

  return (
    <div className="space-y-1.5">
      {/* REPLAY MODE: GEX Frozen Indicator */}
      {isReplayFrozen && (
        <div className="flex items-center justify-center py-1.5 rounded bg-orange-500/20 border border-orange-500/50">
          <span className="text-orange-400 text-[10px] font-mono font-semibold">
            🔁 GEX FROZEN (Replay Mode - Tick Data Only)
          </span>
        </div>
      )}
      
      {/* Estimated Indicator - Compact (only if NOT in replay mode) */}
      {isEstimated && !isReplayFrozen && (
        <div className="flex items-center justify-center py-1 rounded bg-yellow-500/10 border border-yellow-500/30">
          <span className="text-yellow-500 text-[9px] font-mono">
            ⚠️ Estimated Levels (No Option Chain Data)
          </span>
        </div>
      )}
      
      {/* Expiry Date - Compact (Prices now in Sidebar) */}
      {expiry_date && !isReplayFrozen && (
        <div className="flex items-center justify-center text-[9px] text-muted-foreground">
          <span>Expiry: {expiry_date}</span>
        </div>
      )}
      
      {/* NET GEX Summary - Compact */}
      <div className={`bg-gradient-to-r from-buy/10 via-surface/30 to-sell/10 border rounded p-1.5 ${isReplayFrozen ? 'border-orange-500/30 opacity-60' : 'border-border/50'}`}>
        <div className="flex items-center justify-between">
          <div className="text-center flex-1">
            <div className="text-[8px] text-muted-foreground uppercase">Put Wall GEX</div>
            <div className={`text-sm font-bold font-mono ${isReplayFrozen ? 'text-muted-foreground' : (put_wall_gex >= 0 ? 'text-buy' : 'text-sell')}`}>
              {isReplayFrozen ? '—' : `${put_wall_gex >= 0 ? '+' : ''}${put_wall_gex?.toFixed(2) || '0'}Cr`}
            </div>
          </div>
          <div className="text-center px-2">
            <div className="text-[8px] text-muted-foreground uppercase">Net GEX</div>
            <div className={`text-base font-bold font-mono ${isReplayFrozen ? 'text-muted-foreground' : (totalNetGex >= 0 ? 'text-buy' : 'text-sell')}`}>
              {isReplayFrozen ? '—' : `${totalNetGex >= 0 ? '+' : ''}${totalNetGex?.toFixed(2) || '0'}Cr`}
            </div>
          </div>
          <div className="text-center flex-1">
            <div className="text-[8px] text-muted-foreground uppercase">Call Wall GEX</div>
            <div className={`text-sm font-bold font-mono ${isReplayFrozen ? 'text-muted-foreground' : (call_wall_gex >= 0 ? 'text-buy' : 'text-sell')}`}>
              {isReplayFrozen ? '—' : `${call_wall_gex >= 0 ? '+' : ''}${call_wall_gex?.toFixed(2) || '0'}Cr`}
            </div>
          </div>
        </div>
      </div>
      
      {/* Regime Badge - Compact (Show "NEUTRAL" in replay mode) */}
      <div className={`flex items-center justify-center py-1 rounded ${
        isReplayFrozen
          ? 'bg-orange-500/10 border border-orange-500/30'
          : isNegativeGamma 
            ? 'bg-sell/20 border border-sell/40' 
            : 'bg-buy/20 border border-buy/40'
      }`}>
        <span className={`font-mono text-[10px] font-bold ${
          isReplayFrozen ? 'text-orange-400' : (isNegativeGamma ? 'text-sell' : 'text-buy')
        }`}>
          {isReplayFrozen ? '🔁 NEUTRAL (FROZEN)' : (isNegativeGamma ? '🔴 NEGATIVE GAMMA' : '🟢 POSITIVE GAMMA')}
        </span>
        {!isReplayFrozen && (
          <span className="ml-1.5 text-[9px] text-muted-foreground">
            ({isNegativeGamma ? 'Volatile/Breakout' : 'Stable/Mean-Revert'})
          </span>
        )}
      </div>

      {/* GEX Levels Grid - Compact */}
      <div className="grid grid-cols-3 gap-1">
        {/* Put Wall (Support) */}
        <div className="bg-buy/10 border border-buy/30 rounded p-1.5 text-center">
          <div className="text-[8px] text-buy/70 uppercase font-semibold">Put Wall</div>
          <div className="text-sm font-bold text-buy font-mono">{put_wall || '-'}</div>
          <div className="text-[8px] text-muted-foreground">
            {put_wall_gex > 0 ? `+${put_wall_gex?.toFixed(2)}Cr` : `${put_wall_gex?.toFixed(2)}Cr`}
          </div>
          <div className="text-[8px] text-muted-foreground">
            {distanceToPutWall > 0 ? `↑${distanceToPutWall}` : `↓${Math.abs(distanceToPutWall)}`}
          </div>
        </div>

        {/* Zero Gamma (Flip) */}
        <div className="bg-white/5 border border-white/30 rounded p-1.5 text-center">
          <div className="text-[8px] text-white/70 uppercase font-semibold">Zero Gamma</div>
          <div className="text-sm font-bold text-white font-mono">{zero_gamma || '-'}</div>
          <div className="text-[8px] text-muted-foreground">Flip Level</div>
          <div className="text-[8px] text-muted-foreground">
            {distanceToZeroGamma > 0 ? `↑${distanceToZeroGamma}` : `↓${Math.abs(distanceToZeroGamma)}`}
          </div>
        </div>

        {/* Call Wall (Resistance) */}
        <div className="bg-sell/10 border border-sell/30 rounded p-1.5 text-center">
          <div className="text-[8px] text-sell/70 uppercase font-semibold">Call Wall</div>
          <div className="text-sm font-bold text-sell font-mono">{call_wall || '-'}</div>
          <div className="text-[8px] text-muted-foreground">
            {call_wall_gex?.toFixed(2)}Cr
          </div>
          <div className="text-[8px] text-muted-foreground">
            {distanceToCallWall > 0 ? `↑${distanceToCallWall}` : `↓${Math.abs(distanceToCallWall)}`}
          </div>
        </div>
      </div>
      
      {/* Greeks Source Indicator - Admin Only - Compact */}
      {isAdmin && (
        <div className={`flex items-center justify-between text-[9px] px-1.5 py-0.5 rounded ${
          isLiveGexSource 
            ? 'bg-green-500/10 border border-green-500/30' 
            : 'bg-yellow-500/10 border border-yellow-500/30'
        }`}>
          <div className="flex items-center gap-1">
            <span className={isLiveGexSource ? 'text-green-400' : 'text-yellow-400'}>
              {isLiveGexSource ? '✓ LIVE' : '⚠ B-S'}
            </span>
          </div>
          <div className="text-muted-foreground">
            {accelpix_greeks_count > 0 && <span className="text-green-400/70">{accelpix_greeks_count}A</span>}
            {accelpix_greeks_count > 0 && bs_greeks_count > 0 && <span className="mx-0.5">|</span>}
            {bs_greeks_count > 0 && <span className="text-yellow-400/70">{bs_greeks_count}BS</span>}
          </div>
        </div>
      )}
    </div>
  );
});

GEXLevelsPanel.displayName = 'GEXLevelsPanel';


// ============================================
// SUPER SIGNAL PANEL (with God Score Breakdown)
// ============================================

export const SuperSignalPanel = memo(({ superSignal, hftSignal, godScore, marketProfile }) => {
  // Use God Score if available, then Super Signal, then HFT signal
  const score = godScore || superSignal || hftSignal;
  
  if (!score) {
    return (
      <div className="panel-content flex items-center justify-center text-muted-foreground">
        <span className="text-sm font-mono">Waiting for God Score...</span>
      </div>
    );
  }

  // Extract God Score data
  const {
    total_score = 0,
    hft_score = 0,
    gamma_score = 0,
    profile_score = 0,
    direction = "NEUTRAL",
    signal_state = "NEUTRAL",
    should_execute = false,
    is_vetoed = false,
    veto_reason = "",
    active_factors = [],
    hft_breakdown = {},
    gamma_breakdown = {},
    profile_breakdown = {},
    config = {},
  } = score;

  // Fallback to superSignal/hftSignal format if godScore not available
  const displayDirection = direction || score.signal || "NEUTRAL";
  const displayState = signal_state || score.signal_state || "NEUTRAL";
  const displayScore = total_score || score.super_score || score.probability || 0;

  // Determine signal state styling
  const getSignalStyle = () => {
    if (is_vetoed) return { bg: 'bg-orange-500/20', border: 'border-orange-500/50', text: 'text-orange-500' };
    if (displayState === 'EXECUTE') return { bg: 'bg-accent/20', border: 'border-accent', text: 'text-accent' };
    if (displayState === 'BUILDING') return { bg: 'bg-warning/20', border: 'border-warning', text: 'text-warning' };
    return { bg: 'bg-muted/20', border: 'border-muted', text: 'text-muted-foreground' };
  };

  const style = getSignalStyle();
  const threshold = config?.trigger_threshold || 80;

  // Calculate progress bar widths (percentage of max weight)
  const hftMax = config?.weights?.hft || 60;
  const gammaMax = config?.weights?.gamma || 20;
  const profileMax = config?.weights?.profile || 20;

  return (
    <div className="space-y-2">
      {/* Main Signal Display */}
      <div className={`${style.bg} ${style.border} border rounded-lg p-2 text-center`}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] uppercase font-semibold text-muted-foreground">
            {is_vetoed ? '🚫 BLOCKED' : displayState}
          </span>
          <span className="text-[10px] text-muted-foreground font-mono">
            Threshold: {threshold}
          </span>
        </div>
        
        <div className={`text-2xl font-bold font-mono ${style.text}`}>
          {is_vetoed ? 'VETO' : displayDirection}
        </div>
        
        {/* Total Score */}
        <div className="text-3xl font-bold font-mono text-foreground mt-1">
          {displayScore}
          <span className="text-sm text-muted-foreground">/100</span>
        </div>
        
        {/* Veto Reason */}
        {is_vetoed && veto_reason && (
          <div className="text-[10px] text-orange-400 mt-1">{veto_reason}</div>
        )}
      </div>

      {/* === GOD SCORE BREAKDOWN === */}
      <div className="space-y-1.5 bg-surface/50 rounded p-2">
        <div className="text-[10px] uppercase text-muted-foreground font-semibold mb-1">
          Score Breakdown
        </div>
        
        {/* HFT Score Bar */}
        <div className="space-y-0.5">
          <div className="flex justify-between text-[10px]">
            <span className="text-cyan-400">HFT</span>
            <span className="font-mono text-foreground">{hft_score}/{hftMax}</span>
          </div>
          <div className="h-2 bg-surface rounded overflow-hidden">
            <div 
              className="h-full bg-cyan-500 transition-all duration-300"
              style={{ width: `${(hft_score / hftMax) * 100}%` }}
            />
          </div>
          {/* HFT Sub-breakdown */}
          <div className="flex gap-2 text-[9px] text-muted-foreground pl-2">
            {hft_breakdown.imbalance > 0 && <span className="text-cyan-300">Imb: +{hft_breakdown.imbalance}</span>}
            {hft_breakdown.velocity > 0 && <span className="text-cyan-300">Vel: +{hft_breakdown.velocity}</span>}
            {hft_breakdown.sweeps > 0 && <span className="text-cyan-300">Swp: +{hft_breakdown.sweeps}</span>}
            {Object.values(hft_breakdown).every(v => v === 0) && <span>--</span>}
          </div>
        </div>

        {/* Gamma Score Bar */}
        <div className="space-y-0.5">
          <div className="flex justify-between text-[10px]">
            <span className="text-green-400">GAMMA</span>
            <span className="font-mono text-foreground">{gamma_score}/{gammaMax}</span>
          </div>
          <div className="h-2 bg-surface rounded overflow-hidden">
            <div 
              className="h-full bg-green-500 transition-all duration-300"
              style={{ width: `${(gamma_score / gammaMax) * 100}%` }}
            />
          </div>
          {/* Gamma Sub-breakdown */}
          <div className="flex gap-2 text-[9px] text-muted-foreground pl-2">
            {gamma_breakdown.wall_bounce > 0 && <span className="text-green-300">Wall: +{gamma_breakdown.wall_bounce}</span>}
            {gamma_breakdown.roof_reject > 0 && <span className="text-green-300">Roof: +{gamma_breakdown.roof_reject}</span>}
            {gamma_breakdown.gamma_flush > 0 && <span className="text-green-300">Flush: +{gamma_breakdown.gamma_flush}</span>}
            {Object.values(gamma_breakdown).every(v => v === 0) && <span>--</span>}
          </div>
        </div>

        {/* Profile (dPOC) Score Bar */}
        <div className="space-y-0.5">
          <div className="flex justify-between text-[10px]">
            <span className="text-purple-400">dPOC</span>
            <span className="font-mono text-foreground">{profile_score}/{profileMax}</span>
          </div>
          <div className="h-2 bg-surface rounded overflow-hidden">
            <div 
              className="h-full bg-purple-500 transition-all duration-300"
              style={{ width: `${(profile_score / profileMax) * 100}%` }}
            />
          </div>
          {/* Profile Sub-breakdown */}
          <div className="flex gap-2 text-[9px] text-muted-foreground pl-2">
            {profile_breakdown.dpoc_bounce > 0 && <span className="text-purple-300">Bounce: +{profile_breakdown.dpoc_bounce}</span>}
            {profile_breakdown.at_dpoc > 0 && <span className="text-purple-300">At dPOC: +{profile_breakdown.at_dpoc}</span>}
            {Object.values(profile_breakdown || {}).every(v => v === 0 || !v) && <span>--</span>}
          </div>
        </div>
      </div>

      {/* Active Factors List */}
      {active_factors.length > 0 && (
        <div className="text-[9px] text-muted-foreground bg-surface/30 rounded p-1.5 max-h-16 overflow-y-auto">
          <div className="font-semibold uppercase mb-0.5 text-foreground">Active Factors:</div>
          {active_factors.map((f, i) => (
            <div key={i} className="font-mono">{f}</div>
          ))}
        </div>
      )}

      {/* dPOC Value Display */}
      {marketProfile?.dpoc > 0 && (
        <div className="flex items-center justify-between text-[10px] bg-purple-500/10 border border-purple-500/30 rounded px-2 py-1">
          <span className="text-purple-400 font-semibold">📍 dPOC Level</span>
          <span className="font-mono text-foreground">{marketProfile.dpoc?.toLocaleString()}</span>
        </div>
      )}
    </div>
  );
});

SuperSignalPanel.displayName = 'SuperSignalPanel';


// ============================================
// TRIDENT EXIT PANEL
// ============================================

export const TridentPanel = memo(({ tridentStatus, tradingStats }) => {
  const stats = tridentStatus?.stats || tradingStats;
  const currentTier = tridentStatus?.current_tier || 'NONE';
  const riskParams = stats?.risk_params;

  // Tier visualization - abbreviated for narrow panels
  const tiers = [
    { key: 'TIER_1', icon: '🛡️', label: 'SHIELD', desc: 'Stop', active: currentTier === 'TIER_1' },
    { key: 'TIER_2', icon: '💰', label: 'BANKER', desc: 'Lock', active: currentTier === 'TIER_2' },
    { key: 'TIER_3', icon: '🏃', label: 'TRAIL', desc: 'Opt', active: currentTier === 'TIER_3' },
  ];

  const pnl = stats?.total_pnl ?? tradingStats?.total_pnl ?? 0;
  const winRate = stats?.win_rate ?? tradingStats?.win_rate ?? 0;
  const totalTrades = stats?.total_trades ?? tradingStats?.total_trades ?? 0;

  return (
    <div className="space-y-2">
      {/* Trident Tier Visualization - Compact */}
      <div className="grid grid-cols-3 gap-1">
        {tiers.map((tier) => (
          <div 
            key={tier.key}
            className={`text-center py-1.5 px-1 rounded transition-all ${
              tier.active 
                ? 'bg-accent/30 border-2 border-accent' 
                : 'bg-muted/10 border border-muted/30 opacity-60'
            }`}
            title={`${tier.label}: ${tier.desc}`}
          >
            <div className={`text-base leading-none ${tier.active ? 'animate-pulse' : ''}`}>
              {tier.icon}
            </div>
            <div className={`text-[8px] font-bold mt-0.5 truncate ${tier.active ? 'text-accent' : 'text-muted-foreground'}`}>
              {tier.label}
            </div>
          </div>
        ))}
      </div>

      {/* Special Exits */}
      {(currentTier === 'P0' || currentTier === 'HFT_EXIT') && (
        <div className={`text-center py-1.5 rounded ${
          currentTier === 'P0' ? 'bg-sell/30 border-2 border-sell' : 'bg-orange-500/30 border-2 border-orange-500'
        }`}>
          <div className="text-sm animate-pulse">
            {currentTier === 'P0' ? '⚠️ BREAK' : '💀 EXIT'}
          </div>
          <div className="text-[8px] text-muted-foreground">
            {currentTier === 'P0' ? 'Level Breached' : 'HFT < 40%'}
          </div>
        </div>
      )}

      {/* Stats Grid - Compact */}
      <div className="grid grid-cols-3 gap-1 text-center">
        <div className="bg-surface rounded py-1 px-0.5">
          <div className={`text-sm font-bold font-mono ${pnl >= 0 ? 'text-buy' : 'text-sell'}`}>
            {pnl >= 0 ? '+' : ''}{pnl?.toFixed(0)}
          </div>
          <div className="text-[8px] text-muted-foreground">P&L</div>
        </div>
        <div className="bg-surface rounded py-1 px-0.5">
          <div className="text-sm font-bold font-mono text-foreground">{winRate?.toFixed(0)}%</div>
          <div className="text-[8px] text-muted-foreground">Win</div>
        </div>
        <div className="bg-surface rounded py-1 px-0.5">
          <div className="text-sm font-bold font-mono text-foreground">{totalTrades}</div>
          <div className="text-[8px] text-muted-foreground">Trades</div>
        </div>
      </div>

      {/* Risk Params - Compact */}
      {riskParams && (
        <div className="text-[8px] text-muted-foreground font-mono bg-surface/50 rounded p-1.5">
          <div className="flex justify-between gap-1">
            <span>SL:{riskParams.hard_stop}</span>
            <span>TP:{riskParams.profit_target}</span>
            <span>TR:{riskParams.trail_trigger}</span>
          </div>
          {riskParams.is_expiry_day && (
            <div className="text-warning text-center mt-0.5">⚠️ EXPIRY</div>
          )}
        </div>
      )}

      {/* Open Positions - Compact */}
      {stats?.open_positions?.length > 0 && (
        <div className="border-t border-border pt-1.5">
          <div className="text-[8px] uppercase text-muted-foreground mb-0.5">Open</div>
          {stats.open_positions.slice(0, 2).map((pos, i) => (
            <div key={i} className="flex justify-between text-[9px] font-mono bg-surface/50 rounded px-1 py-0.5 mb-0.5">
              <span className={pos.direction === 'LONG' ? 'text-buy' : 'text-sell'}>
                {pos.direction === 'LONG' ? '↑' : '↓'} {pos.quantity}L
              </span>
              <span>E:{pos.entry_price?.toFixed(0)}</span>
              <span>S:{pos.stop_loss?.toFixed(0)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

TridentPanel.displayName = 'TridentPanel';


// ============================================
// GEX CHART OVERLAY DATA GENERATOR
// ============================================

export const useGEXChartOverlay = (gexLevels) => {
  return useMemo(() => {
    if (!gexLevels) return { putWallLine: [], callWallLine: [], zeroGammaLine: [] };

    const { put_wall, call_wall, zero_gamma, timestamp } = gexLevels;
    // Use timestamp from gexLevels as base time
    const baseTime = timestamp || Math.floor(new Date().getTime() / 1000);

    // Create horizontal line data for chart overlays
    // Each line needs at least 2 points with same value but different times
    const createLine = (value) => {
      if (!value) return [];
      return [
        { time: baseTime - 3600, value },
        { time: baseTime + 3600, value },
      ];
    };

    return {
      putWallLine: createLine(put_wall),
      callWallLine: createLine(call_wall),
      zeroGammaLine: createLine(zero_gamma),
    };
  }, [gexLevels]);
};

export default { GEXLevelsPanel, SuperSignalPanel, TridentPanel, useGEXChartOverlay };
